import React from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { appImages } from "../assets/appicons";
import { EMP_BASE_PATH } from "../config";

// ===== Tông màu lấy từ mobile-app-v2 (themes/colors/default.ts) =====
export const APP = {
  primary100: "#2168DA",
  primary200: "#4081EB",
  tabActive: "#2E90FA",
  gray: "#808CA0",
  black: "#18212F",
  textPrimary: "#08142D",
  grey200: "#A4AFC1",
  surface: "#FFFFFF",
  cardBorder: "rgba(8, 20, 45, 0.06)",
  appBg: "#F6F8FC",
  headerGradient: "linear-gradient(180deg, #68A2FF 0%, #2168DA 100%)",
  centerGradient: "linear-gradient(180deg, #68A2FF 0%, #2168DA 100%)",
};

// Icon: nếu truyền `color` -> dùng CSS mask để tô màu (giống tintColor của RN);
// nếu không -> hiển thị PNG nguyên bản (dùng cho icon tab bar nhiều màu).
export const AppIcon = ({ src, size = 24, color, style }) => {
  if (color) {
    return (
      <Box
        as="span"
        display="inline-block"
        w={`${size}px`}
        h={`${size}px`}
        bg={color}
        style={{
          WebkitMaskImage: `url(${src})`,
          maskImage: `url(${src})`,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskPosition: "center",
          maskPosition: "center",
          WebkitMaskSize: "contain",
          maskSize: "contain",
          ...style,
        }}
      />
    );
  }
  return (
    <img
      src={src}
      alt=""
      width={size}
      height={size}
      style={{ objectFit: "contain", display: "block", ...style }}
    />
  );
};

// Ô chức năng (icon vuông bo góc + nhãn) khớp IconTile của app
export const IconTile = ({ icon, label, onClick, tint = APP.tabActive }) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    direction="column"
    align="center"
    w="100%"
    gap="0"
  >
    <Flex
      w="56px"
      h="56px"
      borderRadius="12px"
      bg={APP.surface}
      border="1px solid"
      borderColor={APP.cardBorder}
      boxShadow="0px 6px 10px rgba(8,20,45,0.06)"
      align="center"
      justify="center"
    >
      <AppIcon src={icon} size={25} color={tint} />
    </Flex>
    <Box h="16px" />
    <Text
      fontSize="12px"
      fontWeight={500}
      color={APP.black}
      textAlign="center"
      lineHeight="1.25"
      whiteSpace="pre-line"
    >
      {label}
    </Text>
  </Flex>
);

// Lưới 4 cột khớp app (gap 14)
export const TileGrid = ({ children }) => (
  <Box
    mt="12px"
    display="grid"
    gridTemplateColumns="repeat(4, 1fr)"
    columnGap="14px"
    rowGap="18px"
  >
    {children}
  </Box>
);

// Một cột chỉ số trong thẻ "Đánh giá cá nhân trong tháng"
export const EvalMetric = ({ label, value, icon, iconColor, valueColor, isStatus }) => (
  <Flex direction="column" align="center" flex="1" px="4px" minW="0">
    <Box h="28px" display="flex" alignItems="flex-start" justifyContent="center">
      <Text
        fontSize="10px"
        lineHeight="13px"
        color={APP.grey200}
        textAlign="center"
        noOfLines={2}
      >
        {label}
      </Text>
    </Box>
    <Box h="30px" display="flex" alignItems="center" justifyContent="center">
      <Text
        fontSize={isStatus ? "11px" : "14px"}
        lineHeight={isStatus ? "14px" : "17px"}
        fontWeight={700}
        color={valueColor || APP.black}
        textAlign="center"
        noOfLines={isStatus ? 2 : 1}
      >
        {value}
      </Text>
    </Box>
    <Box h="18px" display="flex" alignItems="center" justifyContent="center">
      {icon ? <AppIcon src={icon} size={14} color={iconColor} /> : null}
    </Box>
  </Flex>
);

// ===== Thanh tab dưới cùng (chỉ hiển thị ở mobile) - khớp mobile-app-v2 =====
const TAB_ITEMS = [
  { key: "home", label: "Trang chủ", icon: appImages.icon_home, iconActive: appImages.icon_home_fill, to: EMP_BASE_PATH },
  { key: "finance", label: "Tài chính", icon: appImages.icon_tai_chinh, iconActive: appImages.icon_tai_chinh_fill },
  { key: "checkin", label: "Chấm công", center: true, to: `${EMP_BASE_PATH}/chuyen-can` },
  { key: "news", label: "Tin tức", icon: appImages.icon_new, iconActive: appImages.icon_new_fill },
  { key: "account", label: "Tài khoản", icon: appImages.icon_account, iconActive: appImages.icon_account_fill, to: `${EMP_BASE_PATH}/tai-khoan` },
];

// Chỉ hiển thị tab bar ở các màn "tab" gốc (giống app), ẩn ở màn con.
const TAB_ROUTES = [
  EMP_BASE_PATH,
  `${EMP_BASE_PATH}/chuyen-can`,
  `${EMP_BASE_PATH}/tai-khoan`,
];

export const MobileTabBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  if (!TAB_ROUTES.includes(path)) return null;

  const isActive = (item) => {
    if (item.key === "home") return path === EMP_BASE_PATH;
    if (item.key === "checkin") return path === `${EMP_BASE_PATH}/chuyen-can`;
    if (item.key === "account") return path === `${EMP_BASE_PATH}/tai-khoan`;
    return false;
  };

  return (
    <Box
      display={{ base: "block", md: "none" }}
      position="fixed"
      left="50%"
      transform="translateX(-50%)"
      bottom="0"
      w="100%"
      maxW="480px"
      h="72px"
      zIndex={30}
      backgroundImage={`url(${appImages.bg_union})`}
      backgroundSize="100% 100%"
      backgroundRepeat="no-repeat"
      pointerEvents="auto"
    >
      <Flex h="100%" align="flex-end" pb="8px">
        {TAB_ITEMS.map((item) => {
          const active = isActive(item);
          if (item.center) {
            return (
              <Flex key={item.key} flex="1" direction="column" align="center" position="relative">
                <Flex
                  as="button"
                  type="button"
                  onClick={() => item.to && navigate(item.to)}
                  position="absolute"
                  top="-26px"
                  w="48px"
                  h="48px"
                  borderRadius="full"
                  bgImage={APP.centerGradient}
                  align="center"
                  justify="center"
                  boxShadow="0 10px 14px rgba(46,144,250,0.35)"
                >
                  <AppIcon src={appImages.icon_checkin} size={32} color="#FFFFFF" />
                </Flex>
                <Text mt="26px" fontSize="10px" fontWeight={500} color={active ? APP.primary100 : APP.gray}>
                  {item.label}
                </Text>
              </Flex>
            );
          }
          return (
            <Flex
              key={item.key}
              as="button"
              type="button"
              onClick={() => item.to && navigate(item.to)}
              flex="1"
              direction="column"
              align="center"
              gap="4px"
            >
              <AppIcon src={active ? item.iconActive : item.icon} size={24} />
              <Text fontSize="10px" fontWeight={active ? 700 : 500} color={active ? APP.primary100 : APP.gray}>
                {item.label}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
};

// ===== Thanh tiêu đề màn con (back + tiêu đề canh giữa) - khớp Header của app =====
export const AppBar = ({ title, onBack, right }) => {
  const navigate = useNavigate();
  return (
    <Flex
      position="sticky"
      top="0"
      zIndex={20}
      align="center"
      justify="center"
      h="52px"
      px="12px"
      bg={APP.surface}
      borderBottom="1px solid"
      borderColor={APP.cardBorder}
    >
      <Flex
        as="button"
        type="button"
        onClick={onBack || (() => navigate(-1))}
        position="absolute"
        left="12px"
        w="36px"
        h="36px"
        align="center"
        justify="center"
      >
        <AppIcon src={appImages.icon_chevron_left} size={22} color={APP.textPrimary} />
      </Flex>
      <Text fontSize="15px" fontWeight={600} color={APP.textPrimary} noOfLines={1} px="44px">
        {title}
      </Text>
      {right ? (
        <Box position="absolute" right="12px">
          {right}
        </Box>
      ) : null}
    </Flex>
  );
};
